import React, { FC, useEffect } from 'react';
import { useAppSelector } from 'redux/store';

const RECORD_SCOPED_PATH = /\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
const SAFE_PATH_AFTER_SWITCH = '/dashboard';

type TImpersonateUpdatesLayerProps = {
  children: React.ReactNode;
};

export const ImpersonateUpdatesLayer: FC<TImpersonateUpdatesLayerProps> = ({ children }) => {
  const { impersonateOn } = useAppSelector((state) => state.usersSlice);
  const currentImpersonatedUserId = impersonateOn ? localStorage.getItem('lastImpersonatedUserId') : null;

  const onStorageListen = (e: StorageEvent) => {
    if (e.key !== 'lastImpersonatedUserId') {
      return;
    }
    const newUserId = e.newValue;
    const switched = !!newUserId && newUserId !== currentImpersonatedUserId;
    const exited = !newUserId && impersonateOn;
    if (!switched && !exited) {
      return;
    }
    // A path scoped to one landlord's record cannot survive the switch - the
    // arriving landlord cannot read it, so reloading in place only shows an error
    if (RECORD_SCOPED_PATH.test(window.location.pathname)) {
      window.location.replace(SAFE_PATH_AFTER_SWITCH);
      return;
    }
    window.location.reload();
  };

  useEffect(() => {
    window.addEventListener('storage', onStorageListen);

    return () => {
      window.removeEventListener('storage', onStorageListen);
    };
  }, [impersonateOn, currentImpersonatedUserId]);

  // Update localStorage whenever impersonation state changes in this tab
  useEffect(() => {
    if (impersonateOn && currentImpersonatedUserId) {
      localStorage.setItem('lastImpersonatedUserId', currentImpersonatedUserId);
    } else if (!impersonateOn) {
      localStorage.removeItem('lastImpersonatedUserId');
    }
  }, [impersonateOn, currentImpersonatedUserId]);

  return <>{children}</>;
};

export default ImpersonateUpdatesLayer;
